import { FLOW_TYPE } from 'constants/flow';
import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Node, useKeyPress, useReactFlow, getConnectedEdges, KeyCode, Edge, XYPosition, useStore } from 'reactflow';
import { getNodeId } from '../utils/nodeUtils';
import useTakeSnapshot from './useTakeSnapshot';

export function useCopyPaste<NodeData, EdgeData>() {
  const mousePosRef = useRef<XYPosition>({ x: 0, y: 0 });
  const rfDomNode = useStore((state) => state.domNode);

  const { getNodes, setNodes, getEdges, setEdges, screenToFlowPosition } = useReactFlow<NodeData, EdgeData>();

  const { takeSnapshot } = useTakeSnapshot();

  // Set up the paste buffers to store the copied nodes and edges.
  const [bufferedNodes, setBufferedNodes] = useState([] as Node<NodeData>[]);
  const [bufferedEdges, setBufferedEdges] = useState([] as Edge<EdgeData>[]);

  // initialize the copy/paste hook
  // 1. remove native copy/paste/cut handlers
  // 2. add mouse move handler to keep track of the current mouse position
  useEffect(() => {
    const events = ['cut', 'copy', 'paste'];

    if (rfDomNode) {
      const preventDefault = (e: Event) => e.preventDefault();

      const onMouseMove = (event: MouseEvent) => {
        mousePosRef.current = {
          x: event.clientX,
          y: event.clientY,
        };
      };

      for (const event of events) {
        rfDomNode.addEventListener(event, preventDefault);
      }

      rfDomNode.addEventListener('mousemove', onMouseMove);

      return () => {
        for (const event of events) {
          rfDomNode.removeEventListener(event, preventDefault);
        }

        rfDomNode.removeEventListener('mousemove', onMouseMove);
      };
    }
  }, [rfDomNode]);

  const copy = useCallback(() => {
    const selectedNodeMap = {} as { [id: string]: Node };
    const groupNodeIds = [] as string[];

    getNodes().forEach((node) => {
      if (node.selected && node.type) {
        if (node.type === FLOW_TYPE.NODE.DATA_NODE) {
          return toast.error('데이터 블록은 복사할 수 없어요');
        }

        selectedNodeMap[node.id] = node;
        if (node.type === FLOW_TYPE.NODE.GROUP_NODE) {
          groupNodeIds.push(node.id);
        }
      }
    });

    if (groupNodeIds.length > 0) {
      const searchedGroupNodeIds = [] as string[];
      const nodes = getNodes();

      groupNodeIds.forEach((id) => {
        if (!searchedGroupNodeIds.includes(id)) {
          const idStack = [id];

          while (idStack.length > 0) {
            const cntId = idStack.pop();

            nodes.forEach((node) => {
              if (node.parentNode === cntId) {
                if (node.type === FLOW_TYPE.NODE.GROUP_NODE) {
                  idStack.push(node.id);
                }
                selectedNodeMap[node.id] = node;
              }
            });
          }
        }
        searchedGroupNodeIds.push(id);
      });
    }

    const selectedNodes = Object.values(selectedNodeMap);
    const selectedEdges = getConnectedEdges(selectedNodes, getEdges()).filter((edge) => {
      const isExternalSource = selectedNodes.every((n) => n.id !== edge.source);
      const isExternalTarget = selectedNodes.every((n) => n.id !== edge.target);

      return !(isExternalSource || isExternalTarget);
    });

    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);
  }, [getNodes, getEdges]);

  const cut = useCallback(() => {
    const selectedNodes = getNodes().filter((node) => node.selected);
    const selectedEdges = getConnectedEdges(selectedNodes, getEdges()).filter((edge) => {
      const isExternalSource = selectedNodes.every((n) => n.id !== edge.source);
      const isExternalTarget = selectedNodes.every((n) => n.id !== edge.target);

      return !(isExternalSource || isExternalTarget);
    });

    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);

    // A cut action needs to remove the copied nodes and edges from the graph.
    setNodes((nodes) => nodes.filter((node) => !node.selected));
    setEdges((edges) => edges.filter((edge) => !selectedEdges.includes(edge)));
  }, [getNodes, setNodes, getEdges, setEdges]);

  const paste = useCallback(
    (
      { x: pasteX, y: pasteY } = screenToFlowPosition({
        x: mousePosRef.current.x,
        y: mousePosRef.current.y,
      })
    ) => {
      const minX = Math.min(...bufferedNodes.map((s) => (s.positionAbsolute ?? s.position).x));
      const minY = Math.min(...bufferedNodes.map((s) => (s.positionAbsolute ?? s.position).y));

      const idMap = {} as { [id: string]: string };
      const newNodeMap = {} as { [id: string]: Node };

      const newNodes: Node<NodeData>[] = bufferedNodes
        .map((node) => {
          const newId = getNodeId((node.data as any).name);

          const x = pasteX + ((node.positionAbsolute ?? node.position).x - minX);
          const y = pasteY + ((node.positionAbsolute ?? node.position).y - minY);
          const newNode = {
            ...node,
            id: newId,
            positionAbsolute: { x, y },
            ...(!node.parentNode && {
              position: { x, y },
            }),
          };

          idMap[node.id] = newId;
          newNodeMap[node.id] = newNode;

          return newNode;
        })
        .map((node) => {
          if (node.parentNode) {
            const newParentNode = newNodeMap[node.parentNode];

            if (newParentNode) {
              node.parentNode = newParentNode.id;
            } else {
              node.position = node.positionAbsolute;
              delete node.parentNode;
              delete node.extent;
            }
          }

          if (node.type === FLOW_TYPE.NODE.GROUP_NODE) {
            node.data = {
              ...node.data,
              sourceNodeIds: (node.data as any).sourceNodeIds.map((sourceNodeId: string) => idMap[sourceNodeId]),
              targetNodeIds: (node.data as any).targetNodeIds.map((targetNodeId: string) => idMap[targetNodeId]),
            };
          }

          return node;
        });

      const newEdges: Edge<EdgeData>[] = bufferedEdges.map((edge) => {
        const newSource = idMap[edge.source] ?? edge.source;
        const newTarget = idMap[edge.target] ?? edge.target;

        let newId = edge.id;

        if (newSource !== edge.source) {
          newId = newId.replaceAll(edge.source, newSource);
        }
        if (newTarget !== edge.target) {
          newId = newId.replaceAll(edge.target, newTarget);
        }

        return {
          ...edge,
          id: newId,
          source: newSource,
          sourceHandle: `${newSource}_source`,
          target: newTarget,
          targetHandle: `${newTarget}_target`,
        };
      });

      takeSnapshot();
      setNodes((nodes) => [...nodes.map((node) => ({ ...node, selected: false })), ...newNodes]);
      setEdges((edges) => [...edges.map((edge) => ({ ...edge, selected: false })), ...newEdges]);
    },
    [bufferedNodes, bufferedEdges, takeSnapshot, screenToFlowPosition, setNodes, setEdges]
  );

  // useShortcut(['Meta+x', 'Ctrl+x'], cut);
  useShortcut(['Meta+c', 'Control+c', 'Ctrl+x'], copy);
  useShortcut(['Meta+v', 'Control+v', 'Ctrl+v'], paste);

  return { cut, copy, paste, bufferedNodes, bufferedEdges };
}

function useShortcut(keyCode: KeyCode, callback: Function): void {
  const [didRun, setDidRun] = useState(false);
  const shouldRun = useKeyPress(keyCode);

  useEffect(() => {
    if (shouldRun && !didRun) {
      callback();
      setDidRun(true);
    } else {
      setDidRun(shouldRun);
    }
  }, [shouldRun, didRun, callback]);
}

export default useCopyPaste;
